import Logo from "src/components/Logo";
import {
    Box,
    Card,
    CardContent,
    Container,
    Step,
    StepConnector,
    StepLabel,
    Stepper,
    Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import { CheckIcon } from "src/theme/overrides/CustomIcons";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import LanguagePopover from "../main/header/LanguagePopover";

const StepIconStyle = ({ active, completed }: { active: boolean; completed: boolean }) => {
    return (
        <Box
            sx={{
                zIndex: 9,
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: active ? "primary.main" : "text.disabled",
            }}
        >
            {completed ? (
                <CheckIcon sx={{ zIndex: 1, width: 20, height: 20, color: "primary.main" }} />
            ) : (
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "currentColor",
                    }}
                />
            )}
        </Box>
    );
};

const StepStyle = styled(Step)(({ theme }) => ({
    [theme.breakpoints.down("sm")]: {
        width: 120,
        flexBasis: 120,
        flexShrink: 0,
    },
}));

const StepConnectorStyle = styled(StepConnector)(({ theme }) => ({
    top: 10,
    left: "calc(-50% + 20px)",
    right: "calc(50% + 20px)",
    "& .MuiStepConnector-line": {
        borderTopWidth: 2,
        borderColor: theme.palette.divider,
    },
    "&.Mui-active, &.Mui-completed": {
        "& .MuiStepConnector-line": {
            borderColor: theme.palette.primary.main,
        },
    },
}));

const LayoutMainBox = styled(Box)(() => ({
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",
}));

const LayoutGradient = styled(Box)(() => ({
    opacity: 0.5,
    backgroundImage:
        "linear-gradient(180deg, rgba(255,238,224,0.15) 0%, rgba(255,217,189,0.15) 100%)",
    width: "178vw",
    aspectRatio: "1.62",
    borderRadius: "50%",
    position: "absolute",
    bottom: "45%",
    left: 0,
    zIndex: -1,
    pointerEvents: "none",
}));

const LayoutContainer = ({ children }: { children: ReactNode }) => {
    return (
        <Container
            maxWidth="md"
            sx={{
                maxWidth: "md",
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                paddingRight: { xs: 0, sm: 2 },
                paddingLeft: { xs: 0, sm: 2 },
            }}
        >
            {children}
        </Container>
    );
};

const LayoutCopyright = ({ children }: { children: ReactNode }) => {
    return (
        <Typography
            variant="caption"
            sx={{
                py: 4,
                px: { xs: 3, sm: 0 },
                color: "text.disabled",
                textAlign: "center",
                textTransform: "uppercase",
                letterSpacing: 1.1,
            }}
        >
            {children}
        </Typography>
    );
};

const CompanyWizzardLayout: FC = ({ children }) => {
    const { t } = useTranslation();
    const params = useParams();

    const StepLabels = [
        t("auth.companyWizzard.steps.configuration"),
        t("auth.companyWizzard.steps.subscription"),
        t("auth.companyWizzard.steps.done"),
    ];

    const activeStep = params?.step === "subscription" ? 1 : params?.step === "done" ? 2 : 0;

    return (
        <LayoutMainBox>
            <LayoutGradient />
            <Box
                sx={{
                    pt: { xs: 2, sm: 6 },
                    px: { xs: 0, sm: 8 },
                    margin: { xs: "0 auto", sm: 0 },
                    maxWidth: { xs: 120, sm: "none" },
                }}
            >
                {params?.step && (
                    <Stepper
                        alternativeLabel
                        activeStep={activeStep}
                        connector={<StepConnectorStyle />}
                        sx={{ transform: { xs: `translateX(-${activeStep * 120}px)`, sm: "none" } }}
                    >
                        {Object.values(StepLabels).map((label) => (
                            <StepStyle key={label}>
                                <StepLabel
                                    StepIconComponent={StepIconStyle}
                                    sx={{
                                        "& .MuiStepLabel-label": {
                                            typography: "subtitle2",
                                            color: "text.disabled",
                                        },
                                    }}
                                >
                                    {label}
                                </StepLabel>
                            </StepStyle>
                        ))}
                    </Stepper>
                )}
            </Box>
            <LayoutContainer>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        pt: 4,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            marginBottom: 3.5,
                            position: "relative",
                        }}
                    >
                        <Logo />
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                right: 16,
                                zIndex: 50,
                            }}
                        >
                            <LanguagePopover />
                        </Box>
                    </Box>
                    <Card elevation={16}>
                        <CardContent>{children}</CardContent>
                    </Card>
                </Box>
                <LayoutCopyright>
                    {t("shared.copyright").replace(":year", new Date().getFullYear())}
                </LayoutCopyright>
            </LayoutContainer>
        </LayoutMainBox>
    );
};

export default CompanyWizzardLayout;
